import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const products = [
  {
    id: 'edx-hat-001',
    name: 'EDX Construction Hat',
    price: '$24.99',
    image: 'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/jjsa2f1m_bone.jpg.jpeg',
    description: 'Premium quality construction hat with EDX logo',
  },
  {
    id: 'edx-tshirt-001',
    name: 'EDX Construction T-Shirt',
    price: '$29.99',
    image: 'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/f0n781jc_camisa.jpg.jpeg',
    description: 'Comfortable cotton t-shirt with EDX branding',
  },
  {
    id: 'edx-hoodie-001',
    name: 'EDX Construction Hoodie',
    price: '$49.99',
    image: 'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/vki97nw1_moletom.jpg.jpeg',
    description: 'Warm and stylish hoodie with EDX logo',
  },
];

export const Shop = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div data-testid="shop-page">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-[hsl(215,75%,16%)] to-[hsl(224,64%,33%)] text-white text-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ShoppingBag className="w-16 h-16 mx-auto mb-6" />
          <h1
            className="text-5xl sm:text-6xl font-black mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            {t('shop.title')}
          </h1>
          <p className="text-xl sm:text-2xl text-white/90">
            {t('shop.subtitle')}
          </p>
          {getCartCount() > 0 && (
            <div className="mt-8">
              <Button
                onClick={() => navigate('/cart')}
                className="btn-premium btn-premium-secondary inline-flex items-center gap-2"
                data-testid="view-cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                {t('shop.viewCart')} ({getCartCount()})
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden premium-card-hover"
                data-testid={`product-card-${index}`}
              >
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3
                    className="text-xl font-bold text-[hsl(215,75%,16%)] mb-2"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-[hsl(215,75%,16%)]">
                      {product.price}
                    </span>
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="btn-premium btn-premium-primary text-sm px-6 py-3"
                      data-testid={`product-buy-${index}`}
                    >
                      {t('shop.addToCart')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Coming Soon Notice */}
          <div className="mt-16 text-center">
            <p className="text-lg text-gray-600">
              {t('shop.comingSoon')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
